import {
  castToSnapshot,
  getParent,
  types,
  getType,
} from 'mobx-state-tree';
import { createThunk, model } from 'mst-collection';

import { schemas } from '@api';
import {
  ExtendedListModel,
  getLikeApiByParentEntity,
  handleFlow,
  shouldRejectPagination,
} from '@models/utils';
import { UserModel } from '@models/Viewer/UserModel';
import { LikeParentEntityEnum } from '@constants';

export class LikesUserList extends ExtendedListModel(
  types.reference(types.late(() => UserModel)),
  {
    totalCount: 0,
  },
  {
    idAttribute: 'id',
    schema: schemas.UserListSchema,
    pageSize: 30,
  },
) {
  get parentEntityName(): LikeParentEntityEnum {
    const name = getType(this.parentEntity).name;

    // This case should never happen
    if (!Object.values(LikeParentEntityEnum).includes(name as any)) {
      console.error(
        // eslint-disable-next-line max-len
        'Seems that you are using likes for not configured entity, please take a look at LikeParentEntityEnum and getLikeApiByParentEntity',
      );
    }

    return name as LikeParentEntityEnum;
  }

  get parentEntityId() {
    return this.parentEntity.id;
  }

  get parentEntity() {
    return getParent<{ id: number }>(this);
  }

  private get _LikesApi() {
    return getLikeApiByParentEntity(this.parentEntityName);
  }

  fetch = createThunk(() => {
    return async function fetch(this: LikesUserList) {
      const res = await this._LikesApi.getPostLikes({
        id: this.parentEntityId,
        count: this.pageSize,
        start: 0,
      });

      this.set(res.data.users);
    };
  });

  fetchMore = createThunk(() => {
    return async function fetchMore(this: LikesUserList, flow) {
      if (shouldRejectPagination(this)) {
        return;
      }

      await handleFlow(flow, async () => {
        const res = await this._LikesApi.getPostLikes({
          id: this.parentEntityId,
          count: this.pageSize,
          start: this.offset,
        });

        this.append(castToSnapshot(res.data.users));
      });
    };
  }, false);
}

export const LikesUserListModel = model(LikesUserList);
